import { lifeConflict, lifeIgnore, LIVING_ROLES, type IgnoredTerm, type LifeConflict, type SpanRole, type StoryCtx, type StoryNode } from '@dispositio/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestLive, type SuggestState } from './suggest';

/**
 * Lebt die Figur hier? (AP-42, core `stateConflicts`): Sie spricht, handelt, denkt oder erfährt etwas, ist zu der Zeit aber schon tot bzw.
 * noch nicht geboren – rot an jeder dieser Stellen wie eine Zeitmarke, die nicht mehr stimmt (`suggest.ts`, Art `conflict`). ✕ merkt
 * „ist Absicht“ (ein Geist, eine Vision) für die Figur im Dokument, der weitere Ausweg zeigt die Szene von Tod bzw. Geburt. Nur, was in
 * Weltzeit sicher ist; nur im Draft einer Szene.
 */

export interface LifeHit {
  from: number;
  to: number;
  conflict: LifeConflict;
  key: string;
}

export interface LifeLive extends SuggestLive<LifeHit> {
  ctx: StoryCtx;
  scene: StoryNode | null;
  /** Element zu einem Verweisschlüssel */
  entityOf: (key: string) => string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: LifeHit) => string;
  other: (hit: LifeHit) => { label: string; title: string } | null;
}

export const lifeConflictKey = new PluginKey<SuggestState<LifeHit>>('lifeConflicts');

function lifeHits(doc: PMNode, l: LifeLive): LifeHit[] {
  const scene = l.scene;
  if (!scene) return [];
  const known = new Map<string, LifeConflict | null>();
  const conflictOf = (id: string): LifeConflict | null => {
    if (!known.has(id)) {
      const c = lifeConflict(l.ctx, scene, id);
      const entry = lifeIgnore(l.ctx, id, 'text');
      known.set(id, c?.certainty === 'sure' && !l.ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after) ? c : null);
    }
    return known.get(id)!;
  };
  const out: LifeHit[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const span = node.marks.find((m) => m.type.name === 'refSpan' && LIVING_ROLES.includes(m.attrs.role as SpanRole));
    if (!span) return false;
    for (const key of span.attrs.keys as string[]) {
      const id = l.entityOf(key);
      const c = id ? conflictOf(id) : null;
      if (!c) continue;
      // ein Bezug über mehrere Textstücke (etwa mit einem Verweis darin) zählt einmal
      const last = out[out.length - 1];
      if (last && last.to === pos && last.conflict.entityId === c.entityId) last.to = pos + node.nodeSize;
      else out.push({ from: pos, to: pos + node.nodeSize, conflict: c, key });
      break;
    }
    return false;
  });
  return out;
}

export const lifeConflictMarks = (live: { current: LifeLive }) =>
  suggestExtension<LifeHit, LifeLive>(
    {
      name: 'lifeConflicts',
      kind: 'conflict',
      key: lifeConflictKey,
      base: 'rt-conflict',
      hits: lifeHits,
      mark: (h, l) => ({ class: `rt-life-conflict ${h.conflict.state}`, 'data-life-conflict': h.conflict.state, title: l.title(h) }),
      labels: (_h, l) => ({ accept: null, ignore: l.labels.ignore }),
      other: (h, l) => l.other(h),
      word: (h) => h.key,
      testId: 'life',
    },
    live,
  );
